import axios from 'axios';
import authService from './authService';
import config from '../config/environment';

// Prevent multiple simultaneous logout attempts
let logoutInProgress = false;

const api = axios.create({
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use(
  (reqConfig) => {
    const origin = typeof window !== 'undefined'
      ? window.location.origin.replace(/\/$/, '')
      : '';
    const apiUrl = config.API_URL.replace(/\/$/, '');
    // Same-origin relative paths in production; absolute base URL for local split-stack dev
    reqConfig.baseURL = apiUrl && apiUrl !== origin ? apiUrl : '';
    const token = authService.getToken();
    if (token) {
      reqConfig.headers.Authorization = `Bearer ${token}`;
    }
    // FormData must use browser-generated multipart boundary
    if (typeof FormData !== 'undefined' && reqConfig.data instanceof FormData) {
      if (reqConfig.headers && typeof reqConfig.headers.delete === 'function') {
        reqConfig.headers.delete('Content-Type');
      } else if (reqConfig.headers) {
        delete reqConfig.headers['Content-Type'];
      }
    }
    return reqConfig;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle 401 Unauthorized (expired tokens) and 504 Gateway Timeout
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.config?.url?.includes('/admin/persona-webhooks')) {
      console.error('Persona webhook API error:', { status: error.response?.status, code: error.code });
    } else {
      console.error('API Error:', error);
    }
    
    // Prevent multiple simultaneous logouts
    if (logoutInProgress) {
      return Promise.reject(error);
    }
    
    const authService = require('./authService').default;
    const hadToken = Boolean(error.config?.headers?.Authorization);

    // If 401 Unauthorized, force logout and reload
    if (error.response && error.response.status === 401) {
      logoutInProgress = true;
      console.log('🔒 Received 401 Unauthorized - forcing logout');
      authService.logout();
      if (hadToken) {
        alert('Your session has expired. Please log in again.');
      }
      window.location.reload();
    }
    
    // If 504 Gateway Timeout (backend instance issue), force logout and retry
    if (error.response && error.response.status === 504) {
      logoutInProgress = true;
      console.log('⏱️ Received 504 Gateway Timeout - forcing logout to retry with fresh instance');
      authService.logout();
      alert('Authentication service is experiencing issues. Please log in again to retry.');
      window.location.reload();
    }
    
    return Promise.reject(error);
  }
);

/**
 * Routing API - Uses existing backend /external-route endpoint
 * ✅ IMPLEMENTED IN BACKEND
 */
export const routingAPI = {
  // Send a message through the routing system (non-streaming)
  sendMessage: async (message, sessionId, userEmail, routeType = 'Ringo', category = 'Both') => {
    const response = await api.post('/api/v1/external-route', {
      message,
      Route_Type: routeType,
      Category: { type: category },
      session_id: sessionId,
      user_email: userEmail,
      timestamp: new Date().toISOString(),
      trace_id: `trace_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    });
    return response;
  },

  // Health check - ✅ IMPLEMENTED
  healthCheck: async () => {
    const response = await api.get('/health');
    return response;
  },
};

/**
 * Launch an external AI tool (Account Plan, PCAD, Deal Designer, Sales Pipeline, or generic env_url).
 * Handles SSO for Account Plan via same-origin postMessage (never put JWTs in the URL).
 * PCAD, Deal Designer, and Sales Pipeline are integrated routes within Ringo Web.
 * @param {Object} agent - Agent object with agent_id/id, agent_name/name, env_url
 * @param {Object} authService - authService instance (getUserInfo)
 */
export function launchExternalAgent(agent, authService) {
  const agentName = ((agent.agent_name ?? agent.name) ?? '').toLowerCase().trim();
  const envUrl = (agent.env_url || '').toLowerCase();

  // Check if URL is external (starts with http/https and points to external domain)
  // This prevents false positives where tool names contain keywords like "account plan"
  // but are actually external tools (e.g., Gemini gems, external apps)
  const isExternalUrl = envUrl.startsWith('http://') || envUrl.startsWith('https://');
  const isExternalDomain = isExternalUrl && !envUrl.includes(window.location.hostname);

  // If this is an external URL to a different domain, launch it directly
  // Examples: Gemini gems (gemini.google.com), external apps, third-party tools
  if (isExternalDomain) {
    window.open(agent.env_url, '_blank', 'noopener,noreferrer');
    return;
  }

  // Only do name-based routing for internal tools or relative paths
  const isAccountPlanGenerator = agentName === 'account plan generator' || agentName.includes('account plan');
  const isPcadBrdGenerator = (agentName.includes('pcad') && (agentName.includes('brd') || agentName.includes('generator'))) || envUrl.includes('/pcad');
  const isDealDesigner = agentName === 'deal designer' || agentName.includes('deal designer');
  const isSalesPipeline = agentName === 'sales priority pipeline' || agentName.includes('sales pipeline') || agentName.includes('ringo ai board') || agentName.includes('sales iq') || agentName.includes('ringo sales iq');
  const isRcAccountIntelligence =
    envUrl.includes('/rc-account-intelligence') ||
    agentName.includes('rc account intelligence') ||
    agentName.includes('account intelligence');
  const isLegalTranslator = agentName === 'legal translator' || agentName.includes('legal translator');

  let targetUrl;
  if (isAccountPlanGenerator) {
    targetUrl = `${window.location.origin}/account-plan`;
  } else if (isPcadBrdGenerator) {
    targetUrl = `${window.location.origin}/pcad`;
  } else if (isDealDesigner) {
    targetUrl = `${window.location.origin}/deal-designer`;
  } else if (isSalesPipeline) {
    // Route to integrated Sales Pipeline view (same as Deal Designer pattern)
    targetUrl = `${window.location.origin}/sales-pipeline`;
  } else if (isRcAccountIntelligence) {
    targetUrl = `${window.location.origin}/rc-account-intelligence`;
  } else if (isLegalTranslator) {
    targetUrl = `${window.location.origin}/legal-translator`;
  } else {
    targetUrl = agent.env_url || '#';
  }

  // PCAD, Deal Designer, Sales Pipeline, RC Account Intelligence, Legal Translator: integrated route in new tab
  if (isDealDesigner || isPcadBrdGenerator || isSalesPipeline || isRcAccountIntelligence || isLegalTranslator) {
    window.open(targetUrl, '_blank', 'noopener,noreferrer');
    return;
  }

  if (isAccountPlanGenerator && authService) {
    const userInfo = authService.getUserInfo?.();
    const accessToken = authService.getToken();
    const params = new URLSearchParams({
      apiUrl: process.env.REACT_APP_ACCOUNT_PLAN_API_URL || 'http://localhost:8001',
      salesforceEnv: process.env.REACT_APP_SALESFORCE_ENVIRONMENT || 'prd',
    });
    const finalUrl = `${targetUrl}?${params.toString()}`;
    const newWindow = window.open(finalUrl, '_blank');
    if (newWindow && userInfo) {
      const messageOrigin = window.location.origin;
      const checkWindow = setInterval(() => {
        try {
          if (newWindow.closed) {
            clearInterval(checkWindow);
            return;
          }
          newWindow.postMessage({
            type: 'OKTA_USER_INFO',
            userInfo,
            accessToken,
            timestamp: Date.now(),
          }, messageOrigin);
          clearInterval(checkWindow);
        } catch (_) {}
      }, 100);
      setTimeout(() => clearInterval(checkWindow), 5000);
    }
    return;
  }

  if (targetUrl && targetUrl !== '#') {
    window.open(targetUrl, '_blank', 'noopener,noreferrer');
  }
}

/**
 * Conversations API - Thread/History management
 * Combines both naming conventions for maximum compatibility
 */
export const conversationsAPI = {
  // Get recent conversations for a user
  getConversations: async (userEmail, limit = 10) => {
    const response = await api.get('/api/v1/conversations', {
      params: { user_email: userEmail, limit },
    });
    return response;
  },

  // Search conversations for a user
  searchConversations: async (userEmail, searchQuery, limit = 10) => {
    const response = await api.get('/api/v1/conversations', {
      params: { user_email: userEmail, limit, search: searchQuery },
    });
    return response;
  },

  // Get conversation thread details by ID
  getConversationThread: async (threadId, userEmail) => {
    const response = await api.get(`/api/v1/conversations/thread/${threadId}`, {
      params: { user_email: userEmail },
    });
    return response;
  },

  // Get user's conversation list with pagination
  getList: async (userEmail, limit = 50, offset = 0) => {
    const response = await api.get('/api/v1/conversations/', {
      params: { user_email: userEmail, limit, offset },
    });
    return response;
  },

  // Search conversations with fuzzy matching
  search: async (userEmail, query, limit = 50, offset = 0) => {
    const response = await api.get('/api/v1/conversations/search', {
      params: { user_email: userEmail, q: query, limit, offset },
    });
    return response;
  },

  // Delete a conversation
  delete: async (sessionId, userEmail) => {
    const response = await api.delete(`/api/v1/conversations/${sessionId}`, {
      params: { user_email: userEmail },
    });
    return response;
  },
};

/**
 * Feedback API - User feedback on chat messages
 * ✅ IMPLEMENTED IN BACKEND
 */
export const feedbackAPI = {
  // Submit feedback for a message
  submitFeedback: async (sessionId, traceId, isPositive, feedbackText = null, uuid = null, userEmail = null, conversationId = null, agentId = null) => {
    const response = await api.post('/api/v1/feedback/', {
      session_id: sessionId,
      trace_id: traceId,
      conversation_id: conversationId,
      user_feedback_response: isPositive,
      user_feedback_response_text: feedbackText,
      uuid: uuid,
      user_email: userEmail,
      agent_id: agentId,
    });
    return response;
  },

  // Get feedbacks for a session
  getSessionFeedbacks: async (sessionId, userEmail = null) => {
    const params = {};
    if (userEmail) params.user_email = userEmail;
    const response = await api.get(`/api/v1/feedback/session/${sessionId}`, { params });
    return response;
  },

  // Delete feedback for a message
  deleteFeedback: async (sessionId, conversationId, userEmail = null) => {
    const response = await api.delete('/api/v1/feedback/', {
      data: {
        session_id: sessionId,
        conversation_id: conversationId,
        user_email: userEmail,
      },
    });
    return response;
  },
};

/**
 * Broadcast API
 * ❌ NOT YET IMPLEMENTED IN BACKEND
 * TODO: Backend endpoint needed: POST /api/v1/broadcast
 */
export const broadcastAPI = {
  // Send a broadcast message to all users
  send: async (message, agent = null, userId = null) => {
    const response = await api.post('/api/v1/broadcast', {
      message,
      agent,
      user_id: userId,
    });
    return response;
  },
};

/**
 * Workday API - User location and timezone data
 */
export const workdayAPI = {
  // Get user data from Workday (location, timezone, etc.)
  getUserData: async (userEmail) => {
    try {
      const response = await api.get(`/api/v1/workday/user/${encodeURIComponent(userEmail)}`);
      return response.data?.data || null;
    } catch (error) {
      console.warn('Workday API error:', error);
      return null;
    }
  },
};

/**
 * RingCentral Integration API - User search and sharing
 */
export const ringCentralAPI = {
  // Search RingCentral users by email or name
  searchUsers: async (query) => {
    try {
      const response = await api.get('/api/v1/ringcentral/search-users', {
        params: { query }
      });
      return response.data;
    } catch (error) {
      console.error('RingCentral user search error:', error);
      return [];
    }
  },

  // Share tool to a RingCentral user
  shareToUser: async (recipientEmail, toolData) => {
    try {
      const response = await api.post('/api/v1/ringcentral/share-tool', {
        recipientEmail,
        toolId: toolData.toolId,
        toolTitle: toolData.toolTitle,
        toolDescription: toolData.toolDescription,
        toolUrl: toolData.toolUrl,
        senderEmail: toolData.senderEmail
      });
      return response.data;
    } catch (error) {
      console.error('RingCentral share error:', error);
      throw error;
    }
  },
};

// Legacy alias for backward compatibility with existing code
// Uses threadId in function signatures but maps to sessionId internally
export const chatAPI = {
  // Map to new API methods with parameter transformation
  sendMessage: async (message, sessionId, userId) => {
    return routingAPI.sendMessage(message, sessionId, userId, 'Ringo', 'Both');
  },
  getChatHistory: async (sessionId, userId) => {
    return conversationsAPI.getHistory(sessionId, userId);
  },
  getUserThreads: async (userId, limit = 50, offset = 0) => {
    return conversationsAPI.getList(userId, limit, offset);
  },
  searchThreads: async (userId, query, limit = 50, offset = 0) => {
    return conversationsAPI.search(userId, query, limit, offset);
  },
  deleteThread: async (sessionId, userId) => {
    return conversationsAPI.delete(sessionId, userId);
  },
  healthCheck: async () => {
    return routingAPI.healthCheck();
  },
};

export const crawlerAPI = {
  // Get all crawlers with pagination
  // Returns: { data: [...], totalCount: number }
  getCrawlers: async (skip = 0, limit = 50, isActive = null) => {
    const params = { skip, limit };
    if (isActive !== null) {
      params.is_active = isActive;
    }
    const response = await api.get('/api/v1/crawlers/', { params });
    // Extract X-Total-Count header
    const totalCount = parseInt(response.headers['x-total-count'] || '0', 10);
    return { ...response, totalCount };
  },

  // Create a new crawler
  // Throws 409 error if URL already exists (error.response.data.error_code === 'DUPLICATE_URL')
  createCrawler: async (crawlerData, userId = null) => {
    const params = userId ? { user_id: userId } : {};
    const response = await api.post('/api/v1/crawlers/', crawlerData, { params });
    return response;
  },

  // Get a specific crawler by ID
  getCrawler: async (crawlerId) => {
    const response = await api.get(`/api/v1/crawlers/${crawlerId}`);
    return response;
  },

  // Update a crawler
  // Throws 409 error if URL already exists (error.response.data.error_code === 'DUPLICATE_URL')
  updateCrawler: async (crawlerId, crawlerData, userId = null) => {
    const params = userId ? { user_id: userId } : {};
    const response = await api.patch(`/api/v1/crawlers/${crawlerId}`, crawlerData, { params });
    return response;
  },

  // Delete a crawler
  deleteCrawler: async (crawlerId) => {
    const response = await api.delete(`/api/v1/crawlers/${crawlerId}`);
    return response;
  },

  // Search crawlers
  searchCrawlers: async (query, skip = 0, limit = 50) => {
    const params = { skip, limit };
    if (query) {
      params.query = query;
    }
    const response = await api.get('/api/v1/crawlers/search/', { params });
    return response;
  },
};

/**
 * Agent Configuration API
 * CRUD operations for AI agent configurations
 */
export const agentAPI = {
  // Get all agents with optional filters
  // Returns: { agents: [...], total: number, active_count: number }
  getAgents: async (environment = null, isActive = null, a2aCompatibility = null) => {
    const params = {};
    if (environment) params.environment = environment;
    if (isActive !== null) params.is_active = isActive;
    if (a2aCompatibility !== null) params.a2a_compatibility = a2aCompatibility;
    const response = await api.get('/api/v1/agents', { params });
    return response;
  },

  // Get active agents only (public endpoint)
  // a2aCompatibility: true = landing page agents, false = external agents with env_url
  getActiveAgents: async (environment = null, a2aCompatibility = null) => {
    const params = {};
    if (environment) params.environment = environment;
    if (a2aCompatibility !== null) params.a2a_compatibility = a2aCompatibility;
    const response = await api.get('/api/v1/agents/active/list', { params });
    return response;
  },

  // Get single agent by ID
  getAgent: async (agentId) => {
    const response = await api.get(`/api/v1/agents/${agentId}`);
    return response;
  },

  // Create a new agent
  createAgent: async (agentData) => {
    const response = await api.post('/api/v1/agents', agentData);
    return response;
  },

  // Update an agent (partial update)
  updateAgent: async (agentId, agentData) => {
    const response = await api.patch(`/api/v1/agents/${agentId}`, agentData);
    return response;
  },

  // Delete an agent
  deleteAgent: async (agentId) => {
    const response = await api.delete(`/api/v1/agents/${agentId}`);
    return response;
  },

  // Toggle agent active status
  toggleAgentActive: async (agentId) => {
    const response = await api.post(`/api/v1/agents/${agentId}/toggle-active`);
    return response;
  },
};

// User Feedback API
export const userFeedbackAPI = {
  submit: async ({ feedbackType, relatedTo, comments, outputComments, screenshot, userEmail }) => {
    const formData = new FormData();
    formData.append('feedback_type', feedbackType);
    if (relatedTo) formData.append('related_to', relatedTo);
    if (comments) formData.append('comments', comments);
    if (outputComments) formData.append('output_comments', outputComments);
    if (userEmail) formData.append('user_email', userEmail);
    if (screenshot) formData.append('screenshot', screenshot);

    const response = await api.post('/api/v1/user-feedbacks/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response;
  },
};

export default api;
